import { FC, useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Box, Button } from '@rover/kibble/core';
import Modal from '@rover/kibble/official-patterns/Modal';
import Advanced from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Advanced';
import {
  advancedFiltersCounter,
  shouldShowControls,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { lockPriceToggle } from '@rover/react-lib/src/pages/search/SearchPage/utilities/shouldShowControls';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useGateValue } from '@rover/rsdk/src/modules/Statsig';
import useIsBHUserShim from '@rover/shared/js/hooks/useIsBHUserShim';
import type { SearchDerivedData } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { getIsPriceTransparency } from '@rover/utilities/priceTransparency';

import {
  SearchTopFiltersAdvancedGroupDismiss,
  SearchTopFiltersAdvancedGroupOpen,
  SearchTopFiltersAdvancedGroupSave,
} from '../../../SimplifiedFiltersBar/analyticsEvents';

type PriceFilterProps = {
  currencyCode: string;
  locale: Locale;
  priceUnit: string;
  priceFilterMinValue: number;
  priceFilterMaxValue: number;
  isIncludingFees?: boolean;
  isStaffOrImpersonating?: boolean;
};

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  searchFilters: Partial<SearchFilters>;
  serviceTypeFilterOptions: SearchFiltersOptions;
  updateFiltersAndFireSearchOnChange: (filters: Partial<SearchFilters>) => void;
  priceFilterProps?: PriceFilterProps;
  queryDerivedData: SearchDerivedData;
  countryCode: string;
  isShowingPriceInCurrency: boolean;
  toggleIsShowingPriceInCurrency: (toggle: boolean) => void;
};

const BooleanFiltersModal: FC<Props> = ({
  isOpen,
  onClose,
  searchFilters,
  serviceTypeFilterOptions,
  updateFiltersAndFireSearchOnChange,
  priceFilterProps,
  queryDerivedData,
  countryCode,
  isShowingPriceInCurrency,
  toggleIsShowingPriceInCurrency,
}) => {
  const { t } = useLingui();
  const {
    showStarSitterFilter,
    showLowBookingFilter,
    starSitterLink,
    cardTreatment,
    priceTransparencyLevel,
  } = queryDerivedData;
  const isPriceTransparency = getIsPriceTransparency(priceTransparencyLevel);
  const [pendingFilters, setPendingFilters] = useState<Partial<SearchFilters>>({});
  const isBHUser = useIsBHUserShim();

  const { showPrice, showStarSitterOnlyFilter } = shouldShowControls({
    serviceOptions: serviceTypeFilterOptions,
    showStarSitterFilter,
    isPriceTransparencyFull: isPriceTransparency,
    isIncludingFees: priceFilterProps?.isIncludingFees,
    isStaffOrImpersonating: priceFilterProps?.isStaffOrImpersonating,
    isBHUser,
  });

  const isBhRepeatBookingFlagEnabled = useGateValue('rollout_bright_horizons_recurring');
  const isPriceToggleLocked = lockPriceToggle(
    countryCode,
    searchFilters,
    isBHUser,
    isBhRepeatBookingFlagEnabled
  );

  useEffect(() => {
    if (isBHUser) {
      toggleIsShowingPriceInCurrency(isPriceToggleLocked);
    }
  }, [isBHUser, isPriceToggleLocked, toggleIsShowingPriceInCurrency]);

  useEffect(() => {
    if (isOpen) {
      emitAnalyticsEvent(new SearchTopFiltersAdvancedGroupOpen());
    }
  }, [isOpen]);

  const handleChange = (filter: Partial<SearchFilters>): void => {
    setPendingFilters((prev) => ({ ...prev, ...filter }));
  };

  const handleSave = (): void => {
    if (Object.keys(pendingFilters).length > 0) {
      updateFiltersAndFireSearchOnChange(pendingFilters);
    }
    onClose();
    emitAnalyticsEvent(new SearchTopFiltersAdvancedGroupSave());
    setPendingFilters({});
  };

  const handleDismiss = (): void => {
    onClose();
    emitAnalyticsEvent(new SearchTopFiltersAdvancedGroupDismiss());
    setPendingFilters({});
  };

  const displayFilters = { ...searchFilters, ...pendingFilters };

  const priceOptions =
    priceFilterProps && showStarSitterFilter
      ? {
          minPrice: priceFilterProps.priceFilterMinValue,
          maxPrice: priceFilterProps.priceFilterMaxValue,
        }
      : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleDismiss}
      contentProps={{ testID: 'boolean-filters-modal', width: '600px' }}
    >
      <Box
        maxHeight="75vh"
        sx={{
          overflowY: 'auto',
          // Prevents highlighted content from being hidden behind the modal padding
          px: '1x',
          pb: '1x',
        }}
      >
        <Advanced
          advancedFiltersCount={advancedFiltersCounter(
            displayFilters,
            serviceTypeFilterOptions,
            priceOptions
          )}
          isPriceTransparency={isPriceTransparency}
          priceTransparencyLevel={priceTransparencyLevel}
          onChange={handleChange}
          searchFiltersOptions={serviceTypeFilterOptions}
          searchFilters={displayFilters}
          showLowBookingFilter={!!showLowBookingFilter}
          onClose={handleDismiss}
          priceFilterProps={priceFilterProps}
          starSitterLink={starSitterLink}
          shouldShowPriceFilter={showPrice}
          shouldShowStarSitterFilter={showStarSitterOnlyFilter}
          shouldShowPriceToggle={isBHUser}
          isShowingPriceInCurrency={isShowingPriceInCurrency}
          onToggleShowingPriceInCurrency={toggleIsShowingPriceInCurrency}
          priceToggleLocked={isPriceToggleLocked}
          cardTreatment={cardTreatment}
        />
      </Box>
      <Box mt="4x" display="flex" justifyContent="flex-end">
        <Button size="small" onClick={handleSave} fullWidth variant="primary">
          {t`Save`}
        </Button>
      </Box>
    </Modal>
  );
};

export default BooleanFiltersModal;
